import React, { useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import Icon from 'components/elements/Icon'
import shortid from 'shortid'

import FadeInOut from 'components/transitions/FadeInOut'
import DropIn from 'components/transitions/DropIn'

import { basicKeybindSkips } from 'components/deckPage/modals/KeybindsOverlay'

import styles from './modal.module.scss'

type Props = {
  trigger?: React.ReactNode
  open?: boolean
  onOpen?: () => void
  onClose?: () => void
  children?: React.ReactNode
  modalClassName?: string
  triggerClassName?: string
  closeButton?: boolean
  usedInOverlay?: boolean
}

const Modal = ({
  trigger = null,
  open = false,
  onOpen,
  onClose,
  children = null,
  modalClassName = '',
  triggerClassName = '',
  closeButton = false,
  usedInOverlay = false,
}: Props) => {
  const [internalOpen, setInternalOpen] = useState(open)

  const [onClient, setOnClient] = useState(false)
  const [closeClickStarted, setCloseClickStarted] = useState(false)
  const modalRef = useRef<HTMLDivElement>(typeof document === 'undefined' ? null : document.createElement('div'))
  const openRef = useRef(internalOpen)
  const keylistener = useRef<(e: KeyboardEvent) => void>(() => null)

  useEffect(() => {
    setOnClient(true)

    if (!modalRef.current) return

    modalRef.current.id = `archidekt-modal-${shortid.generate()}`

    // Can this be cleaned up to not have to be in the DOM at all until we need it?
    document.body.appendChild(modalRef.current)

    return () => {
      modalRef.current && document.body.removeChild(modalRef.current)
    }
  }, [])

  useEffect(() => {
    setInternalOpen(open)
  }, [open])

  useEffect(() => {
    if (internalOpen && onOpen) onOpen()

    window.removeEventListener('keydown', keylistener.current)

    keylistener.current = (e: KeyboardEvent) => {
      if (basicKeybindSkips(e)) return

      if (e.key === 'Escape') handleClose(false)
    }

    window.addEventListener('keydown', keylistener.current)

    if (internalOpen) document.body.classList.add('no-scrolling')
    else if (!usedInOverlay) document.body.classList.remove('no-scrolling')

    openRef.current = internalOpen

    setTimeout(() => setInternalOpen(internalOpen), 200)

    return () => {
      if (!usedInOverlay) document.body.classList.remove('no-scrolling')

      window.removeEventListener('keydown', keylistener.current)
    }
  }, [internalOpen])

  const handleClose = (openState = false) => {
    if (onClose) onClose()

    setInternalOpen(openState)
  }

  // Using the built in onClick means that if you start the click in the modal and end it in the dimmer area it closes
  // the modal. This is frustrating when selecting text in an input field. To avoid this we track if the click started
  // in the dimmer area and only close the modal if it did and it also ended in the dimmer area.
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) setCloseClickStarted(true)
    else setCloseClickStarted(false)
  }

  const handleMouseUp = (e: React.MouseEvent<HTMLDivElement>) => {
    if (closeClickStarted && e.target === e.currentTarget) handleClose(false)
    setCloseClickStarted(false)
  }

  return (
    <>
      {trigger && (
        <button className={triggerClassName} onClick={() => setInternalOpen(true)}>
          {trigger}
        </button>
      )}
      {onClient &&
        internalOpen &&
        ReactDOM.createPortal(
          <FadeInOut
            unmountOnClosed
            show={internalOpen}
            className={styles.container}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}>
            <DropIn show className={`${styles.modal} ${modalClassName}`}>
              {closeButton && (
                <button className={styles.closeButton} onClick={() => handleClose(false)}>
                  <Icon name="close" />
                </button>
              )}
              {children}
            </DropIn>
          </FadeInOut>,
          modalRef.current,
        )}
    </>
  )
}
export default Modal
